import {ApolloLink, Observable} from '@apollo/client';

import type {FetchResult, Operation} from '@apollo/client';
import {print, stripIgnoredCharacters, visit} from 'graphql';
import {noop} from '@sail/utils';
import currentVersion from '../../../schema/version';
import {parseQueryParams} from '../../shared/graphql/links/RestLink/parseQueryParams';
import {requestIdleCallback} from '../utils/idleCallback';

export type ComparisonRequest = {
  query: string;
  variables: Record<string, any>;
  compare_request: {
    has_pii: boolean;
    expected_response_payload?: string;
    original_action_id?: string[];
    underlying_request_payload?: {
      method: 'GET' | 'POST' | 'PUT' | 'DELETE';
      endpoint_url: string;
      body: string;
      query_param: Record<string, string>;
    };
  };
};

enum OperationType {
  QUERY = 'query',
  MUTATION = 'mutation',
}

type InternalUnderlyingRequest = {
  '@rest': {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    runtimePath: string;
  };
};

export type OperationResult = FetchResult<
  Record<string, any>,
  Record<string, any>,
  Record<string, any>
>;

const parseQueryString = (query: string): Record<string, string> => {
  const queryString = query.includes('?')
    ? query.slice(query.indexOf('?'))
    : undefined;
  if (!queryString) return {};

  const queryParams: Record<string, string | string[]> =
    parseQueryParams(queryString);
  const parsedParams: Record<string, string> = {};
  Object.entries(queryParams).forEach(([key, value]) => {
    parsedParams[key] = Array.isArray(value) ? value.join(',') : value;
  });
  return parsedParams;
};

const stripQueryDirectives = (query: Operation['query']): string => {
  return stripIgnoredCharacters(
    print(
      visit(query, {
        Directive(node) {
          return node.name.value !== 'include' && node.name.value !== 'skip'
            ? null
            : undefined;
        },
      }),
    ),
  );
};

const GQLG_HEADERS = {
  'Content-Type': 'application/json',
  'Stripe-Version': `v1/${currentVersion}`,
  'X-Stripe-Graphql-Gateway-Priority': 'synthetic',
  Authorization: 'STRIPE-V2-SIG',
} as const;

const buildComparisonRequest = (
  operation: Operation,
  result: OperationResult,
  hasPii: boolean,
  originalActionIds: string[],
): ComparisonRequest => {
  const definition = operation.query.definitions.find(
    (def) => def.kind === 'OperationDefinition',
  )?.operation;

  const body: ComparisonRequest = {
    query: stripQueryDirectives(operation.query),
    variables: operation.variables,
    compare_request: {
      has_pii: hasPii,
      original_action_id: originalActionIds,
    },
  };

  if (definition === OperationType.QUERY) {
    body.compare_request.expected_response_payload = JSON.stringify(result);
  } else {
    const requestFromContext: InternalUnderlyingRequest | undefined =
      operation.getContext()?.underlyingRequest;

    body.compare_request.underlying_request_payload = requestFromContext && {
      method: requestFromContext['@rest'].method,
      endpoint_url: requestFromContext['@rest'].runtimePath,
      body: JSON.stringify(operation.variables.bodyBuilder(requestFromContext)),
      query_param: parseQueryString(
        operation.variables.pathBuilder(requestFromContext),
      ),
    };
  }

  return body;
};

export default function createComparisonV1ToV2Link(
  shadowComparisonOperationsV1ToV2?: Record<string, boolean>,
): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const context = operation.getContext();
    const fetch = context.fetch ?? window.fetch;
    const enabled = shadowComparisonOperationsV1ToV2?.[':VerifyIntegrity'];
    const rnd = Math.random();
    const isSampledForShadowing = rnd < 0.001;
    const isSampledForSchemaValidation = rnd < 0.01;
    if (enabled && isSampledForSchemaValidation) {
      requestIdleCallback(() => {
        const data = {
          query: stripQueryDirectives(operation.query),
          variables: operation.variables,
          gqlg_request_validation: true,
        };
        fetch('/graphql/multiregion', {
          method: 'POST',
          body: JSON.stringify(data),
          headers: GQLG_HEADERS,
        }).catch(noop);
      });
    }

    return new Observable(function comparisonV2ToV2Observer(observer) {
      const subscription = forward(operation).subscribe({
        next: function onNextComparisonV1ToV2Link(data) {
          if (
            shadowComparisonOperationsV1ToV2 &&
            operation.operationName in shadowComparisonOperationsV1ToV2 &&
            isSampledForShadowing
          ) {
            const updatedContext = operation.getContext();
            const responses: Response[] =
              updatedContext.responses ??
              (updatedContext.response ? [updatedContext.response] : []);
            const actionIds = responses
              .map((r) => r.headers?.get('stripe-action-id'))
              .filter((id): id is string => id != null && id !== '');

            const comparisonRequest = buildComparisonRequest(
              operation,
              data,
              shadowComparisonOperationsV1ToV2[operation.operationName],
              actionIds,
            );

            fetch('/graphql/multiregion', {
              method: 'POST',
              body: JSON.stringify(comparisonRequest),
              headers: GQLG_HEADERS,
            }).catch(noop);
          }

          return observer.next(data);
        },
        error: function onErrorComparisonV1ToV2Link(error) {
          return observer.error(error);
        },
        complete: function onCompleteComparisonV1ToV2Link() {
          return observer.complete();
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    });
  });
}
